import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "../../../ui-elements/Tooltip/Tooltip";
import { faCircleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { CountryList } from "../../../assets/staticData/CountryList";

export const boardMemberTypes = ["4", "5", "6", "7"];

export const AthaettusomRikiTooltipColumnHeader = (text: "Ríkisfang" | "Búsetuland" | "Búseta") => <Tooltip tooltipClassName="tooltip-athugunarlisti" tooltip={<div className="tooltip-text">
  <p><strong>Áhættusöm / ósamvinnuþýð ríki</strong></p>
  Tilkynningarskyldum aðilum sem eiga í viðskiptum við aðila sem tengjast áhættusömum og ósamvinnuþýðum ríkjum,
  er skilt að framkvæma aukna áreiðanleikakönnun á þeim í samræmi við 13. og 14. gr. laga nr. 140/2018. <br />
  Kerfið keyrir á móti gagnagrunni Keldunnar sem heldur utanum áhættusöm og ósamvinnuþýð ríki í samræmi við 6. gr. laga nr. 140/2018.
</div>} text={text} />;

export const StjornmalalegTengslPEPTooltipColumnHeader = <Tooltip
  tooltipClassName="viewCompanyAnswers-tooltip"
  tooltip={<div className="tooltip-text">
    <p><strong>Stjórnmálaleg tengsl</strong></p>
    Keldan heldur úti íslenskum PEP lista sem inniheldur einstaklinga sem eru eða hafa verið háttsettir í opinberri þjónustu ásamt nánustu fjölskyldu og samstarfsmönnum á síðustu 12 mánuðum.
    Samkvæmt 1. mgr. 17. gr. peningaþvættislaga ber tilkynningaskyldum aðilum að kanna hvort viðskiptamaður eða raunverulegur eigandi lögaðila sé í áhættuhópi vegna stjórnmálalegra tengsla.
    Sjálfvirk uppfletting kerfisins er þ.a.l. á viðskiptamanni eða þeim sem kemur fram f.h. lögaðila, prókúruhöfum og raunverulegum eiganda/eigendum.</div>}
  text={'Stjórnmálaleg tengsl'} />

export const AlthjodlegirValistarTooltipColumnHeader = <Tooltip
  tooltipClassName="viewCompanyAnswers-tooltip"
  tooltip={<div className="tooltip-text">
    <p><strong>Alþjóðlegir válistar</strong></p>
    Sbr. lög nr. 140/2018 um peningaþvætti og lög nr. 68/2023 um frystingu fjármuna og um framkvæmd alþjóðlegra þvingunaraðgerða,
    ber tilkynningarskyldum aðilum að kanna hvort viðskiptavinir séu á slíkum alþjóðlegum válistum. Listarnir sem kerfið keyrir á móti uppfylla kröfur
    AML5, FATF, UNCAC, JMLSG, UK/FCA og Wolfsberg Group um peningaþvætti, alþjóðlega þvingunar-, viðskiptaþvingunar- og refsiválista.</div>}
  text={"Alþjóðlegir válistar"} />

export const RenderNationalityWithTooltip = (
  Nationality: string | undefined,
  Nationalityflagged: boolean | string | undefined | null
) => {
  // Check if nationality should be flagged
  // We consider it flagged if the value is:
  // - Not undefined
  // - Not boolean false
  // - Not an empty string
  // - Not the string 'false'
  const isNationalityFlagged =
    Nationalityflagged !== null &&
    Nationalityflagged !== undefined &&
    Nationalityflagged !== false &&
    Nationalityflagged !== '' &&
    Nationalityflagged !== 'false';

  return (
    <div className="rikisfang-wrapper">
      <span>{CountryList.find(country => country.value === Nationality)?.label || Nationality}</span>
      {/* Show warning tooltip if nationality is flagged */}
      {isNationalityFlagged &&
        <Tooltip
          tooltipClassName="tooltip-gildandiSkraning"
          noUnderline
          tooltip={
            <div className="tooltip-text">
              Athugið að þetta ríkisfang gæti verið á lista yfir áhættusöm og ósamvinnuþýð ríki.
            </div>
          }
          text={<FontAwesomeIcon icon={faCircleExclamation} />}
        />
      }
    </div>
  );
}

/**
 * Supported question list types.
 * Each type represents a different questionnaire template with its own question ordering.
 */
export type QuestionListType = "KYC_COMPANY_FOREIGN" | "KYC_COMPANY_DOMESTIC" | "KYC_INDIVIDUAL";

export type availableVersions = "1" | "2";

// Add more types as needed

/**
 * The shape of the question order mapping.
 * Each key maps a logical question purpose to its numeric position
 * in the specific questionnaire version/type combination.
 * 
 * Values are nullable — if a question doesn't exist in a given
 * version/type combination, its value will be null.
 */
export interface QuestionOrderMap {
  /** Question about country of registration — uses country code lookup */
  COUNTRY_OF_REGISTRATION: number | null;
  /** Question about high-risk or non-cooperative countries — renders JSON country entries (V2 only) */
  HIGH_RISK_COUNTRIES: number | null;
  /** Question about the nature of business — shows tooltip description (V2 only) */
  BUSINESS_NATURE: number | null;
  /** Question about politically exposed persons — renders person list (V2) or table (V1) */
  POLITICAL_EXPOSURE: number | null;
  /** Question about beneficial owners — shows expandable owners table */
  BENEFICIAL_OWNERS: number | null;
  /** Question about company registry info — shows expandable registry tables */
  COMPANY_REGISTRY: number | null;

  Q2_BUSINESS_WITH_CASH?: number | null; // Example of a new question in V2 that may not exist in V1
  Q8_POLITICAL_DANGER?: number | null; // Example of a new question in V2 that may not exist in V1
  /** Question about tax domicile (individuals) — renders countryCode + TIN from taxDomicile */
  TAX_DOMICILE?: number | null;
}

/**
 * Lookup table defining question order for every version + type combination.
 * 
 * Structure: QUESTION_ORDER_CONFIG[version][type] → QuestionOrderMap
 * 
 * To add a new version or type:
 *   1. Add the type to the QuestionListType union
 *   2. Add the corresponding entry here with the correct order numbers
 *   3. Set any questions that don't exist in that combination to null
 */
const QUESTION_ORDER_CONFIG: Record<availableVersions, Record<QuestionListType, QuestionOrderMap>> = {
  "1": {
    KYC_COMPANY_FOREIGN: {
      COUNTRY_OF_REGISTRATION: 1,
      HIGH_RISK_COUNTRIES: 4,
      BUSINESS_NATURE: 5,
      POLITICAL_EXPOSURE: 7,
      BENEFICIAL_OWNERS: null,
      COMPANY_REGISTRY: null,
    },
    KYC_COMPANY_DOMESTIC: {
      COUNTRY_OF_REGISTRATION: 1, // Not applicable for domestic companies
      HIGH_RISK_COUNTRIES: 4,
      BUSINESS_NATURE: 5,
      POLITICAL_EXPOSURE: 7,
      BENEFICIAL_OWNERS: 8,
      COMPANY_REGISTRY: 10,
    },
    KYC_INDIVIDUAL: {
      COUNTRY_OF_REGISTRATION: null,
      HIGH_RISK_COUNTRIES: 2,
      BUSINESS_NATURE: null,
      POLITICAL_EXPOSURE: 4,
      BENEFICIAL_OWNERS: null,
      COMPANY_REGISTRY: null,
    },
  },
  "2": {
    KYC_COMPANY_FOREIGN: {
      COUNTRY_OF_REGISTRATION: 1,
      Q2_BUSINESS_WITH_CASH: 2, // New question in V2 about cash business, not relevant for special rendering
      HIGH_RISK_COUNTRIES: 4,
      BUSINESS_NATURE: 5,
      POLITICAL_EXPOSURE: 7,
      Q8_POLITICAL_DANGER: 8,
      BENEFICIAL_OWNERS: null,
      COMPANY_REGISTRY: null,
    },
    KYC_COMPANY_DOMESTIC: {
      COUNTRY_OF_REGISTRATION: 1, // Not applicable for domestic companies
      Q2_BUSINESS_WITH_CASH: 2,
      HIGH_RISK_COUNTRIES: 4,
      BUSINESS_NATURE: 5,
      POLITICAL_EXPOSURE: 7,
      Q8_POLITICAL_DANGER: 8,
      BENEFICIAL_OWNERS: 9,
      COMPANY_REGISTRY: 10,
    },
    KYC_INDIVIDUAL: {
      COUNTRY_OF_REGISTRATION: null,
      HIGH_RISK_COUNTRIES: 2,
      BUSINESS_NATURE: null,
      POLITICAL_EXPOSURE: 4,
      BENEFICIAL_OWNERS: null,
      COMPANY_REGISTRY: null,
      TAX_DOMICILE: 3,
    },
  },
};

/**
 * Returns the question order mapping for a given version and questionnaire type.
 * 
 * This replaces the old static QUESTION_ORDER constant, allowing different
 * questionnaire templates to have different question numbering.
 * 
 * If the version or type doesn't match any known configuration, returns a
 * fallback map where all values are null. This prevents runtime errors —
 * no special question rendering will activate, but the basic question/answer
 * display still works since null comparisons simply won't match.
 * 
 * @param version - The layout version ("1" or "2")
 * @param type - The questionnaire type (e.g. "KYC_COMPANY_FOREIGN")
 * @returns QuestionOrderMap with the correct order numbers, or all-null fallback
 */
export const getQuestionOrder = (version: availableVersions, type: QuestionListType): QuestionOrderMap => {
  const fallback: QuestionOrderMap = {
    COUNTRY_OF_REGISTRATION: null,
    HIGH_RISK_COUNTRIES: null,
    BUSINESS_NATURE: null,
    POLITICAL_EXPOSURE: null,
    BENEFICIAL_OWNERS: null,
    COMPANY_REGISTRY: null,
  };

  const result = QUESTION_ORDER_CONFIG[version]?.[type];

  if (!result) {
    console.warn(
      `[getQuestionOrder] No question order config found for version="${version}", type="${type}". ` +
      `Falling back to all-null map — no special question rendering will activate.`
    );
    return fallback;
  }

  return result;
};